/**
 * Google Identity Services, loaded once, on demand.
 *
 * The browser half of "Continue with Google". Google's own button is the only
 * thing in GIS that hands back an ID TOKEN (the token client gives OAuth
 * access tokens, which the server cannot verify as a sign-in), so the page
 * draws Google's button rather than its own: `renderButton` puts an iframe in
 * a box we size, and the callback gets the token, minted for our web client
 * and carrying the nonce the server issued.
 *
 * The page's ORIGIN must be listed under "Authorized JavaScript origins" on
 * that web client in Google Cloud (web.nsibi.app, and http://localhost:3002
 * for dev), or the button opens a Google page saying the origin is not
 * allowed. Nothing on this side can see that failure.
 */
const SRC = 'https://accounts.google.com/gsi/client';

export type GoogleCredential = { credential?: string };

type ButtonOptions = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  locale?: string;
};

type Gis = {
  accounts: {
    id: {
      initialize(options: {
        client_id: string;
        callback: (response: GoogleCredential) => void;
        nonce?: string;
        ux_mode?: 'popup' | 'redirect';
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        itp_support?: boolean;
        use_fedcm_for_button?: boolean;
      }): void;
      renderButton(parent: HTMLElement, options: ButtonOptions): void;
      cancel(): void;
    };
  };
};

declare global {
  interface Window {
    google?: Gis;
  }
}

let loading: Promise<Gis> | null = null;

/** The GIS library, fetched the first time something asks for it. */
export function loadGoogleIdentity(): Promise<Gis> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (loading) return loading;
  loading = new Promise<Gis>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) resolve(window.google);
      else reject(new Error('Google sign-in did not load.'));
    };
    script.onerror = () => {
      // A blocked script (an extension, a network that filters Google) must
      // be retryable on the next mount rather than cached as a failure.
      loading = null;
      script.remove();
      reject(new Error('Google sign-in did not load.'));
    };
    document.head.appendChild(script);
  });
  return loading;
}
